// Selection tracking, pinning, context box, and document navigation.

import {
  currentSelection,
  selectionMeta,
  pinnedSelection,
  setCurrentSelection,
  setSelectionMeta,
  setPinnedSelection,
  findNodeInScan,
} from "./state";
import { resolveSelectionToNodes } from "../core/documentScanner";
import { devLog } from "./messages";

let _selectionBusy = false;

// Read current Word selection text into state
export async function trackSelection() {
  if (_selectionBusy) return;
  _selectionBusy = true;
  try {
    await Word.run(async (context) => {
      const sel = context.document.getSelection();
      sel.load("text");
      await context.sync();

      const text = (sel.text || "").trim();

      if (!text) {
        setCurrentSelection("");
        setSelectionMeta(null);
        return;
      }

      if (text === currentSelection) return;

      setCurrentSelection(text);
      const wordCount = text.split(/\s+/).filter(Boolean).length;
      setSelectionMeta({ wordCount, charCount: text.length });
    });
  } catch {
    // ignore — selection may be collapsed
  } finally {
    _selectionBusy = false;
  }
}

// Pin current selection: resolve to node IDs and show in context box
export async function pinSelection() {
  if (!currentSelection) {
    clearContext();
    return;
  }

  try {
    const resolved = await resolveSelectionToNodes();

    if (!resolved || resolved.nodes.length === 0) {
      setPinnedSelection({
        nodes: [],
        nodeIds: [],
        fullNodeIds: [],
        partialNodeIds: [],
        selectionText: currentSelection,
        wordCount: selectionMeta?.wordCount || 0,
        charCount: selectionMeta?.charCount || 0,
        paraCount: 0,
      });
    } else {
      // Rebuild selectionText from full node texts (not partial fragments)
      const fullText = resolved.nodes
        .map((n) => n.text || "")
        .filter(Boolean)
        .join("\n");
      resolved.selectionText = fullText;
      resolved.wordCount = fullText.split(/\s+/).filter(Boolean).length;
      resolved.charCount = fullText.length;
      setCurrentSelection(fullText);
      setSelectionMeta({ wordCount: resolved.wordCount, charCount: resolved.charCount });
      setPinnedSelection(resolved);
    }

    updateContext(pinnedSelection);
  } catch (err) {
    // devLog("PIN_ERR", err.message);
  }
}

export function unpinSelection() {
  setPinnedSelection(null);
  setCurrentSelection("");
  setSelectionMeta(null);
  clearContext();
}

export function clearContext() {
  document.getElementById("ctx-empty").style.display = "none";
  document.getElementById("ctx-content").style.display = "none";
}

// Populate context box UI with pinned selection info
export function updateContext(pinned) {
  const emptyEl = document.getElementById("ctx-empty");
  const contentEl = document.getElementById("ctx-content");
  const textEl = document.getElementById("ctx-text");
  const paraEl = document.getElementById("ctx-para");
  const wordsEl = document.getElementById("ctx-words");
  const charsEl = document.getElementById("ctx-chars");
  const jumpEl = document.getElementById("ctx-jump");
  const nodeInfoEl = document.getElementById("ctx-nodes");
  const unpinEl = document.getElementById("ctx-unpin");

  emptyEl.style.display = "none";
  contentEl.style.display = "flex";

  let displayText = pinned.selectionText;
  if (pinned.nodes && pinned.nodes.length > 0) {
    const fullTexts = pinned.nodes.map((n) => n.text || "").filter(Boolean);
    if (fullTexts.length > 0) displayText = fullTexts.join("\n");
  }
  const preview =
    displayText.length > 200 ? displayText.slice(0, 200).trimEnd() + "…" : displayText;
  textEl.textContent = preview;

  paraEl.textContent = `${pinned.paraCount} para${pinned.paraCount !== 1 ? "s" : ""}`;
  wordsEl.textContent = `${pinned.wordCount} words`;
  charsEl.textContent = `${pinned.charCount} chars`;

  if (nodeInfoEl) {
    nodeInfoEl.style.display = "none";
  }

  jumpEl.onclick = async (e) => {
    e.preventDefault();
    await locateNodes(pinned);
  };

  if (unpinEl) {
    unpinEl.onclick = (e) => {
      e.preventDefault();
      unpinSelection();
    };
  }
}

// Navigate to pinned nodes by searching first/last node text, selecting the range
export async function locateNodes(pinned) {
  if (!pinned || !pinned.nodes || pinned.nodes.length === 0) {
    if (pinned?.selectionText) return await locateByText(pinned.selectionText);
    return false;
  }

  try {
    let found = false;
    await Word.run(async (context) => {
      const firstNode = pinned.nodes[0];
      const lastNode = pinned.nodes[pinned.nodes.length - 1];

      const firstText = (firstNode.text || "").trim();
      if (!firstText) return;

      const startAnchor = firstText.slice(0, 200);
      const startResults = context.document.body.search(startAnchor, {
        matchCase: false,
        matchWholeWord: false,
      });
      startResults.load("items");
      await context.sync();

      if (startResults.items.length === 0) {
        const shortAnchor = firstText.slice(0, 80);
        const shortResults = context.document.body.search(shortAnchor, {
          matchCase: false,
          matchWholeWord: false,
        });
        shortResults.load("items");
        await context.sync();
        if (shortResults.items.length > 0) {
          for (const item of shortResults.items) item.paragraphs.load("items/style");
          await context.sync();
          const pick = _pickNonTocItem(shortResults.items);
          if (pick) {
            pick.select();
            await context.sync();
            found = true;
          }
        }
        return;
      }

      for (const item of startResults.items) item.paragraphs.load("items/style");
      await context.sync();
      const startPick = _pickNonTocItem(startResults.items);
      if (!startPick) return;

      if (firstNode.id === lastNode.id) {
        startPick.select();
        await context.sync();
        found = true;
        return;
      }

      const endText = (lastNode.text || "").trim();
      const endAnchor = endText.length > 200 ? endText.slice(-200) : endText;
      const endResults = context.document.body.search(endAnchor, {
        matchCase: false,
        matchWholeWord: false,
      });
      endResults.load("items");
      await context.sync();

      if (endResults.items.length > 0) {
        for (const item of endResults.items) item.paragraphs.load("items/style");
        await context.sync();
        const endPick =
          _pickNonTocItem(endResults.items) || endResults.items[endResults.items.length - 1];
        const startRange = startPick.getRange("Start");
        const endRange = endPick.getRange("End");
        const fullRange = startRange.expandTo(endRange);
        fullRange.select();
        await context.sync();
        found = true;
      } else {
        startPick.select();
        await context.sync();
        found = true;
      }
    });
    return found;
  } catch {
    if (pinned.selectionText) return await locateByText(pinned.selectionText);
    return false;
  }
}

// Fallback: locate by raw text search (for unresolved selections)
export async function locateByText(text) {
  if (!text || !text.trim()) return false;
  try {
    let found = false;
    await Word.run(async (context) => {
      const trimmed = text.trim();
      const lines = trimmed
        .split(/[\r\n\t]+/)
        .map((l) => l.trim())
        .filter((l) => l.length > 3);
      if (lines.length === 0) return;

      const startAnchor = lines[0].slice(0, 250);

      const startResults = context.document.body.search(startAnchor, {
        matchCase: false,
        matchWholeWord: false,
      });
      startResults.load("items");
      await context.sync();
      if (startResults.items.length === 0) return;

      for (const item of startResults.items) {
        item.paragraphs.load("items/style");
      }
      await context.sync();

      const pick = _pickNonTocItem(startResults.items);
      if (!pick) return;

      found = true;

      const endAnchor =
        lines[lines.length - 1].length > 250
          ? lines[lines.length - 1].slice(-250)
          : lines[lines.length - 1];

      if (startAnchor === endAnchor || lines.length === 1) {
        pick.select();
        await context.sync();
        return;
      }

      const endResults = context.document.body.search(endAnchor, {
        matchCase: false,
        matchWholeWord: false,
      });
      endResults.load("items");
      await context.sync();

      if (endResults.items.length > 0) {
        for (const item of endResults.items) {
          item.paragraphs.load("items/style");
        }
        await context.sync();
        const endPick =
          _pickNonTocItem(endResults.items) || endResults.items[endResults.items.length - 1];
        const startRange = pick.getRange("Start");
        const endRange = endPick.getRange("End");
        const fullRange = startRange.expandTo(endRange);
        fullRange.select();
        await context.sync();
      } else {
        pick.select();
        await context.sync();
      }
    });
    return found;
  } catch {
    return false;
  }
}

// Locate by scan node ID — uses paragraphIndex for direct nav, falls back to text search
export async function locateByNodeId(nodeId) {
  const node = findNodeInScan(nodeId);
  if (!node) return false;

  const paraIdx = node.position?.paragraphIndex;

  // Direct paragraph navigation with text verification (catches stale indices)
  if (paraIdx != null && paraIdx >= 0) {
    try {
      let found = false;
      await Word.run(async (context) => {
        const paras = context.document.body.paragraphs;
        paras.load("items/text");
        await context.sync();

        if (paraIdx < paras.items.length) {
          const paraText = (paras.items[paraIdx].text || "").trim();
          const nodeText = (node.text || "").trim();
          const paraPrefix = paraText.slice(0, 50).toLowerCase();
          const nodePrefix = nodeText.slice(0, 50).toLowerCase();
          if (paraPrefix === nodePrefix || paraText.includes(nodeText.slice(0, 30))) {
            paras.items[paraIdx].select();
            await context.sync();
            found = true;
          }
        }
      });
      if (found) return true;
    } catch (err) {
      // devLog("LOCATE_NODE_IDX_ERR", err.message);
    }
  }

  // Fallback: text search with TOC skipping
  const fullText = (node.text || "").trim();
  if (!fullText) return false;

  try {
    let found = false;
    await Word.run(async (context) => {
      const searchText = fullText.length > 255 ? fullText.slice(0, 255) : fullText;
      const results = context.document.body.search(searchText, {
        matchCase: false,
        matchWholeWord: false,
      });
      results.load("items");
      await context.sync();

      if (results.items.length > 0) {
        for (const item of results.items) item.paragraphs.load("items/style");
        await context.sync();
        const pick = _pickNonTocItem(results.items);
        if (pick) {
          pick.select();
          await context.sync();
          found = true;
        }
      }
    });
    return found;
  } catch (err) {
    // devLog("LOCATE_NODE_ERR", err.message);
    return false;
  }
}

// Pick first non-TOC search result (falls back to last item if all are TOC)
function _pickNonTocItem(items) {
  if (!items || items.length === 0) return null;
  if (items.length === 1) return items[0];
  for (const item of items) {
    try {
      const paraStyle = (item.paragraphs.items[0]?.style || "").toLowerCase();
      if (!paraStyle.startsWith("toc")) return item;
    } catch {
      return item;
    }
  }
  return items[items.length - 1];
}
